import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Icon, Text, useElementSize, type Omittable } from "@salestrak/ui";

import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import { SanitizedHtml } from "#app/components/editor/sanitized-html";
import { useEditorAutosave } from "#app/components/editor/use-editor-autosave";
import {
  isRichTextEmpty,
  normalizeRichTextValue,
} from "#app/lib/utils/rich-text-value";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { WidgetEmptyState } from "./widget-empty-state";

export type DescriptionWidgetBodyProps = Omittable<{
  /** Identity of the entity being described. Keying the editor on it is what
   *  stops TipTap — which treats `content` as a mount-time seed and ignores
   *  later prop changes — from carrying one entity's document onto the next. */
  entityKey: string;
  value: string;
  /** Absent for a viewer who cannot edit: the prose is plain and an empty
   *  section is one muted line. Resolving marks the write as landed; a sync
   *  write lands at once; a rejection reads as "Not saved". */
  onChange?: (value: string) => void | Promise<unknown>;
  /** The placeholder that IS the empty state. The slash hint is appended. */
  placeholder?: string;
  /** The read-only empty line. */
  emptyMessage?: string;
}>;

/** Where the last edit stands. `pending` is the pause after a keystroke,
 *  before the debounce writes; `saving` is the write in flight. */
type SaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

/** A description taller than this rests COLLAPSED to it: five lines of body
 *  prose (20px leading, 8px block gap) plus the fade band, so the fifth line
 *  reads and the sixth dissolves — enough to know what it is about, never a
 *  wall. A block that spans the cut (a table, an image) dissolves with the
 *  same fade; the cut is a height, not a block count, because blocks are not
 *  lines. Expanded, it runs its full height. */
const DESCRIPTION_COLLAPSED_HEIGHT = 152;

/** A description this close to the cap is not worth a control: the fade
 *  would hide one line and the control would take a line back. */
const DESCRIPTION_COLLAPSE_SLACK = 24;

/** Which descriptions the viewer has expanded, by entity — a reading
 *  preference like a section's collapse, never a fact about the entity. */
const EXPANDED_KEY = "description-expanded";

/**
 * The description's frame: the prose or editor, collapsed to
 * {@link DESCRIPTION_COLLAPSED_HEIGHT} once it outgrows it, over a footer line
 * that carries the save status and, for a long description, the collapse
 * control — an xs outline "Expand" / "Collapse" with a chevron, on the
 * footer's TRAILING edge with the status beside it, so it reads as the
 * section's own trailing control and not as a pill floating in the middle of
 * nothing.
 *
 * Collapsed is a CLIP, never a scroll area of its own: the frame sits inside
 * the drawer's scroller, and a second scroller nested in it catches the wheel
 * on the way down and stops the drawer under the pointer. The fade at the cut
 * says there is more; the control is how it is reached. A description being
 * WRITTEN is always expanded — the caret cannot go behind a clip — so focus in
 * the editable expands the frame for as long as it lasts, and the control
 * reads "Collapse" while it does; pressing it takes focus, which ends the
 * writing and collapses in one move, then keeps the frame in the drawer's
 * view so the control does not leave the pointer behind when the section
 * shrinks.
 *
 * The footer is reserved whenever the frame stands, so a first save never
 * grows the section; it takes the control's 24px only once a description is
 * long enough to collapse.
 */
function DescriptionFrame({
  entityKey,
  writing = false,
  statusLabel = null,
  children,
}: Omittable<{
  entityKey: string;
  /** The editable inside has focus. */
  writing?: boolean;
  statusLabel?: string | null;
  children: React.ReactNode;
}>) {
  const { ref, size } = useElementSize<HTMLDivElement>();
  const frameRef = useRef<HTMLDivElement | null>(null);
  const long = size.height > DESCRIPTION_COLLAPSED_HEIGHT + DESCRIPTION_COLLAPSE_SLACK;
  const { state: expandedByEntity, update } = usePersistedState<Record<string, boolean>>(
    EXPANDED_KEY,
    {},
  );
  const expandedByChoice = expandedByEntity[entityKey] === true;
  const expanded = expandedByChoice || writing;
  const collapsed = long && !expanded;
  const toggle = () => {
    const next = !expanded;
    update({ [entityKey]: next });
    if (!next) {
      // The section is about to lose most of its height under a drawer that
      // may be scrolled past where it will end: keep the frame where the
      // reader can see it once the layout settles.
      requestAnimationFrame(() => frameRef.current?.scrollIntoView({ block: "nearest" }));
    }
  };
  return (
    <>
      {/* ONE element in every state — only the cap changes. Moving the
        * editor to a different parent would remount it (and drop its
        * document and caret) on the very press that expands it. */}
      <Box
        fade={collapsed ? "bottom" : undefined}
        maxHeight={collapsed ? DESCRIPTION_COLLAPSED_HEIGHT : undefined}
        overflow={collapsed ? "hidden" : undefined}
        ref={frameRef}
      >
        <Box ref={ref}>{children}</Box>
      </Box>
      <Box
        align="center"
        direction="row"
        gap="sm"
        height={long ? 24 : 16}
        justify="end"
        paddingX="sm"
      >
        {statusLabel ? (
          <Text aria-live="polite" color="muted" size="xs">
            {statusLabel}
          </Text>
        ) : null}
        {long ? (
          <Button aria-expanded={expanded} onClick={toggle} size="xs" variant="outline">
            <Icon name={expanded ? "chevron-up" : "chevron-down"} size="xs" />
            {expanded ? "Collapse" : "Expand"}
          </Button>
        ) : null}
      </Box>
    </>
  );
}

const SAVE_STATUS_LABEL: Record<SaveStatus, string | null> = {
  idle: null,
  pending: null,
  saving: "Saving…",
  saved: "Saved",
  error: "Not saved",
};

/**
 * A description section's body: the LIVE editor, always, under the section's
 * own title.
 *
 * A description is a field of prose, not a list of items, so it does not
 * borrow the list sections' empty grammar (the "Add …" row that becomes a
 * header when the first item lands). Empty, it is the editor's one placeholder
 * line under the "Description" title, and it grows as text is typed — the
 * section never changes role, so nothing jumps. Full width, no rest state, no
 * hover wash: a press anywhere places the caret, a pointer over a block
 * summons the grip that drags the block. A viewer who cannot edit reads plain
 * prose, or one muted "No description" line.
 *
 * A description that outgrows about five lines rests COLLAPSED to that
 * height — clipped with a fade, never a scroller of its own inside the
 * drawer's — with a quiet "Expand" on the footer's trailing edge; the caret header hides the whole
 * section, this keeps a long one from becoming a wall. Writing in it expands
 * it for as long as the caret is there. Which ones the viewer expanded by
 * choice persists per entity as a reading preference.
 *
 * Writes autosave on a debounce, and a click-away flushes them, so every route
 * out commits what was typed. There is no Save affordance and deliberately no
 * cancel: this is a field, not a form. A status line under the text says where
 * the last edit stands — nothing while typing, "Saving…" once the pause
 * writes, "Saved" once it lands — and clears when focus leaves.
 */
export function DescriptionWidgetBody({
  entityKey,
  value,
  onChange,
  placeholder = "Add a description",
  emptyMessage = "No description",
}: DescriptionWidgetBodyProps) {
  const html = useMemo(() => normalizeRichTextValue(value), [value]);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The write goes through a ref so `save` never changes identity: the
  // autosave flushes whatever it holds the moment its `save` changes, and a
  // host that hands down a fresh closure per render would turn every
  // re-render into a write.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // The editor holds the live document; the autosave writes it after the
  // pause, and the click-away flush writes it at once. The status follows
  // the write, not the keystroke.
  const save = useCallback(async (next: string) => {
    setStatus("saving");
    try {
      await onChangeRef.current?.(next);
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }, []);
  const { onUpdate, setInitialHtml, flush } = useEditorAutosave(save);

  // Seed the saved baseline from the stored value so mounting the editor never
  // writes and only genuine edits flush. Every empty document is the SAME
  // document here and in `handleUpdate`: the editor says "<p></p>" for
  // nothing, a store may hold "" or that string, and neither difference is
  // an edit.
  useEffect(() => {
    setInitialHtml(isRichTextEmpty(html) ? "" : html);
    // The baseline follows the entity, not every keystroke's re-render: `html`
    // is the round-tripped value and re-seeding on it would mark a pending
    // edit as already saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- per-entity baseline seed
  }, [entityKey, setInitialHtml]);

  const handleUpdate = useCallback(
    (next: string) => {
      setStatus("pending");
      onUpdate(isRichTextEmpty(next) ? "" : next);
    },
    [onUpdate],
  );

  // `focused` is the EDITABLE's focus, not the container's: it expands the
  // frame and shows the status. The Collapse pill under the text takes focus
  // on its own press, and that press must read as leaving the text — so the
  // editable's focusout clears it at once, while the commit below still
  // waits for focus to settle outside the whole container.
  const handleFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    if (event.target instanceof HTMLElement && event.target.isContentEditable) {
      setFocused(true);
    }
  }, []);

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (event.target instanceof HTMLElement && event.target.isContentEditable) {
        setFocused(false);
      }
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      // Defer past the complete click: slash-command items and toolbar
      // buttons take focus and hand it straight back, so only a settled
      // focus outside the container is a real click-away.
      blurTimeoutRef.current = setTimeout(() => {
        blurTimeoutRef.current = null;
        if (containerRef.current?.contains(document.activeElement)) return;
        // Leaving IS the commit: the debounce does not get to run on after
        // the reader has moved on.
        flush();
        setStatus("idle");
      }, 0);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    },
    [],
  );

  if (onChange == null) {
    return isRichTextEmpty(html) ? (
      <WidgetEmptyState message={emptyMessage} />
    ) : (
      <Box marginLeftNegative="sm">
        <DescriptionFrame entityKey={entityKey}>
          <Box paddingX="sm" paddingY="xs">
            <SanitizedHtml html={html} />
          </Box>
        </DescriptionFrame>
      </Box>
    );
  }

  const statusLabel = focused ? SAVE_STATUS_LABEL[status] : null;

  // The editor is the section's whole width — pressing anywhere on it places
  // the caret — with its text on the content edge (8px inset bled into the
  // gutter, 4px lead). The frame folds a long description at rest and
  // carries the status line.
  return (
    <Box
      marginLeftNegative="sm"
      onBlur={handleBlur}
      onFocus={handleFocus}
      ref={containerRef}
    >
      <DescriptionFrame entityKey={entityKey} statusLabel={statusLabel} writing={focused}>
        <RichTextEditor
          blockHandles
          compact
          content={html}
          contentPaddingX="sm"
          contentPaddingY="xs"
          fitContent
          key={entityKey}
          label="Description"
          mentions
          onUpdate={handleUpdate}
          placeholder={placeholder}
          shortcutHints
        />
      </DescriptionFrame>
    </Box>
  );
}
